import React, { useState } from 'react'
import { WhatsAppModal } from './WhatsAppModal'
import { useWhatsAppStatus } from '../../contexts/WhatsAppStatusContext'
import { WhatsAppStatusCard } from '../shared/WhatsAppStatusCard'

/* ─── SVG Icons ─────────────────────────────────────────────────────────── */

const LinkIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.86-2.654a4.5 4.5 0 00-1.242-7.244l4.5-4.5a4.5 4.5 0 016.364 6.364l-1.757 1.757" />
  </svg>
)

const CheckCircleIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
)

/* ─── Integration Cards Data ────────────────────────────────────────────── */

interface Integration {
  id: string
  name: string
  description: string
  icon: string
  category: 'crm' | 'messaging' | 'ai' | 'storage' | 'analytics' | 'dev'
  status: 'connected' | 'available' | 'coming_soon'
}

const INTEGRATIONS: Integration[] = [
  { id: 'slack', name: 'Slack', description: 'Send notifications and updates to Slack channels when chats are assigned, escalated, or closed.', icon: '💬', category: 'messaging', status: 'available' },
  { id: 'teams', name: 'Microsoft Teams', description: 'Route alerts and chat summaries to Teams channels for real-time team visibility.', icon: '👥', category: 'messaging', status: 'available' },
  { id: 'hubspot', name: 'HubSpot CRM', description: 'Sync customer data and chat history with HubSpot contacts and deals.', icon: '🔶', category: 'crm', status: 'available' },
  { id: 'salesforce', name: 'Salesforce', description: 'Link support conversations to Salesforce accounts, contacts, and cases.', icon: '☁️', category: 'crm', status: 'coming_soon' },
  { id: 'claude', name: 'Claude AI', description: 'AI-powered reply suggestions, ticket triage, and conversation summaries.', icon: '🤖', category: 'ai', status: 'connected' },
  { id: 'zapier', name: 'Zapier', description: 'Connect WeQuote to 5,000+ apps with no-code automation workflows.', icon: '⚡', category: 'dev', status: 'available' },
  { id: 'google-drive', name: 'Google Drive', description: 'Automatically save chat transcripts and file attachments to Google Drive.', icon: '📁', category: 'storage', status: 'available' },
  { id: 'jira', name: 'Jira', description: 'Create Jira tickets from support conversations for bug tracking and feature requests.', icon: '🎯', category: 'dev', status: 'available' },
  { id: 'segment', name: 'Segment', description: 'Stream support analytics and customer events to your data warehouse.', icon: '📊', category: 'analytics', status: 'coming_soon' },
  { id: 'webhook', name: 'Webhooks', description: 'Send real-time HTTP callbacks for chat events to any custom endpoint.', icon: '🔗', category: 'dev', status: 'available' },
  { id: 'xero', name: 'Xero', description: 'Sync billing data and customer account information with Xero accounting.', icon: '💰', category: 'crm', status: 'coming_soon' },
  { id: 'whatsapp', name: 'OpenWA Gateway', description: 'Accept and respond to WhatsApp messages directly in chats via the self-hosted OpenWA gateway.', icon: '📱', category: 'messaging', status: 'available' },
]

const CATEGORY_LABELS: Record<string, string> = {
  messaging: 'Messaging',
  crm: 'CRM & Sales',
  ai: 'AI & Automation',
  storage: 'Storage',
  analytics: 'Analytics',
  dev: 'Developer Tools',
}

const STATUS_STYLES: Record<string, { label: string; classes: string }> = {
  connected: { label: 'Connected', classes: 'bg-success/10 text-success border-success/20' },
  available: { label: 'Connect', classes: 'bg-brand-600 text-white hover:bg-brand-700 cursor-pointer' },
  coming_soon: { label: 'Coming Soon', classes: 'bg-surface-tertiary text-txt-tertiary border-border cursor-not-allowed' },
}

/* ─── Integrations Page ─────────────────────────────────────────────────── */

export const IntegrationsPage: React.FC = () => {
  const [filter, setFilter] = React.useState<string>('all')
  const [search, setSearch] = React.useState('')
  const [waModalOpen, setWaModalOpen] = useState(false)

  // Reuse the global WhatsApp status (polled every 20s) instead of doing a
  // page-level fetch. Keeps the integration-card status badge in sync with
  // the banner, sidebar dot, and status card.
  const waState = useWhatsAppStatus()
  const waConnected = waState.state === 'connected'
  const checkWaStatus = waState.refresh

  // Re-check when modal closes
  const handleWaModalClose = () => {
    setWaModalOpen(false)
    checkWaStatus()
  }

  const categories = ['all', ...new Set(INTEGRATIONS.map(i => i.category))]

  const filtered = INTEGRATIONS.filter(i => {
    const matchesCategory = filter === 'all' || i.category === filter
    const matchesSearch = !search || i.name.toLowerCase().includes(search.toLowerCase()) || i.description.toLowerCase().includes(search.toLowerCase())
    return matchesCategory && matchesSearch
  })

  const connectedCount = INTEGRATIONS.filter(i => i.status === 'connected').length

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto animate-fade-in">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-txt-primary">Integrations</h1>
        <p className="text-sm text-txt-secondary mt-1">
          Connect WeQuote with your favourite tools and services — {connectedCount} active
        </p>
      </div>

      {/* WhatsApp status card — surfaces critical integration health */}
      <WhatsAppStatusCard onConfigure={() => setWaModalOpen(true)} />


      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
        <div className="relative flex-1 w-full">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-txt-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <input
            type="text"
            placeholder="Search integrations..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-border bg-surface text-txt-primary text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 transition-colors"
          />
        </div>
        <div className="flex gap-1 flex-wrap">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                filter === cat
                  ? 'bg-brand-600 text-white'
                  : 'bg-surface-tertiary text-txt-secondary hover:bg-surface-hover'
              }`}
            >
              {cat === 'all' ? 'All' : CATEGORY_LABELS[cat] || cat}
            </button>
          ))}
        </div>
      </div>

      {/* Integration Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(integration => {
          // Dynamic status override for WhatsApp
          const effectiveStatus = integration.id === 'whatsapp' && waConnected ? 'connected' : integration.status

          return (
            <div
              key={integration.id}
              className="bg-surface rounded-xl border border-border p-5 hover:shadow-md transition-all duration-200 flex flex-col"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{integration.icon}</span>
                  <div>
                    <h3 className="text-sm font-semibold text-txt-primary">{integration.name}</h3>
                    <p className="text-[11px] text-txt-tertiary capitalize">{CATEGORY_LABELS[integration.category]}</p>
                  </div>
                </div>
              </div>
              <p className="text-xs text-txt-secondary leading-relaxed flex-1 mb-4">
                {integration.description}
              </p>
              <button
                disabled={effectiveStatus === 'coming_soon'}
                onClick={() => {
                  if (integration.id === 'whatsapp') setWaModalOpen(true)
                }}
                className={`inline-flex items-center justify-center gap-1.5 w-full px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${STATUS_STYLES[effectiveStatus].classes}`}
              >
                {effectiveStatus === 'connected' && <CheckCircleIcon />}
                {effectiveStatus === 'available' && <LinkIcon />}
                {effectiveStatus === 'connected' && integration.id === 'whatsapp' ? 'Manage' : STATUS_STYLES[effectiveStatus].label}
              </button>
            </div>
          )
        })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-16 text-txt-tertiary">
          <p className="text-sm">No integrations match your search.</p>
        </div>
      )}

      {/* WhatsApp Configuration Modal */}
      <WhatsAppModal open={waModalOpen} onClose={handleWaModalClose} />
    </div>
  )
}
